import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate, useNavigationType } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import QuickContact from "./QuickContact";
import { LightboxProvider } from "./Lightbox";
import { findPage, SITE_URL } from "../siteConfig";
import { reducedMotion, scrollToTarget, startSmoothScroll, stopSmoothScroll } from "../motion/smoothScroll";

// 페이지 전환 막 시간(ms): 덮기 → 페이지 교체 → 걷기
const CURTAIN_IN = 330;
const CURTAIN_OUT = 520;

function setMeta(selector, attr, value) {
  const el = document.head.querySelector(selector);
  if (el) el.setAttribute(attr, value);
}

// 현재 페이지에 맞춰 <html data-ib-page="..."> 와 <body class="ib-page ib-p-..."> 를 붙인다(페이지 CSS 범위).
// 페이지 내용보다 앞에 두어, 각 페이지의 초기 동작(위치 계산 등)이 실행되기 전에 해당 페이지 CSS가 적용되게 한다.
function PageScope({ page, pathname }) {
  useLayoutEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    html.dataset.ibPage = page.key;
    body.classList.add("ib-page", `ib-p-${page.key}`);
    document.title = page.title;
    setMeta('meta[name="description"]', "content", page.description);
    setMeta('meta[name="theme-color"]', "content", page.themeColor);
    setMeta('meta[property="og:title"]', "content", page.title);
    setMeta('meta[property="og:description"]', "content", page.description);
    setMeta('meta[property="og:url"]', "content", SITE_URL + (page.path || pathname));
    return () => body.classList.remove(`ib-p-${page.key}`);
  }, [page, pathname]);
  return null;
}

// 6개 페이지 공통 틀.
// - 사이트 안 링크(<a href="/about"> 등)는 새로고침 없이 이동시키고, 이동 후 스크롤 위치를 맞춘다.
export default function SiteLayout() {
  const location = useLocation();
  const navType = useNavigationType();
  const navigate = useNavigate();
  const page = findPage(location.pathname);

  // 0) 관성 스크롤(PC 마우스 환경만)
  useEffect(() => {
    startSmoothScroll();
    return () => stopSmoothScroll();
  }, []);

  // 1) 스크롤 위치
  //  - 다른 페이지로 이동: 맨 위(주소에 #앵커가 있으면 그 위치)
  //  - 같은 페이지 안 #앵커: 부드럽게 그 위치로
  //  - 뒤로·앞으로 가기: 그 기록에서 보던 위치로
  const positions = useRef(new Map());
  const prevLoc = useRef(null);
  const entryKey = `${location.key}|${location.pathname}${location.search}${location.hash}`;

  useEffect(() => {
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
  }, []);

  useLayoutEffect(() => {
    const onScroll = () => positions.current.set(entryKey, window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [entryKey]);

  useLayoutEffect(() => {
    const prev = prevLoc.current;
    prevLoc.current = location;
    const samePage = !!prev && prev.pathname === location.pathname && prev.search === location.search;
    const jump = (top) => {
      scrollToTarget(top, { immediate: true });
      positions.current.set(entryKey, window.scrollY);
    };
    if (navType === "POP") {
      const saved = positions.current.get(entryKey);
      if (saved != null) return jump(saved);
      if (samePage) return undefined; // 주소창에 #앵커를 직접 친 경우 등은 브라우저에 맡김
    }
    if (location.hash) {
      const el = document.getElementById(decodeURIComponent(location.hash.slice(1)));
      if (el) {
        if (samePage && navType !== "POP") scrollToTarget(el);
        else {
          scrollToTarget(el, { immediate: true });
          positions.current.set(entryKey, window.scrollY);
        }
        return undefined;
      }
    }
    return jump(0);
  }, [entryKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // 2) 페이지 전환 막: 다른 페이지로 갈 때 남색 막이 덮었다가 걷힌다(같은 페이지 안 #앵커 이동은 막 없이)
  const [curtain, setCurtain] = useState("idle");
  const curtainRef = useRef({ state: "idle", pending: null, timers: [] });
  useEffect(() => {
    const c = curtainRef.current;
    return () => c.timers.forEach(clearTimeout);
  }, []);
  const goWithCurtain = useCallback(
    (to) => {
      const c = curtainRef.current;
      if (reducedMotion() || c.state === "out") {
        navigate(to);
        return;
      }
      c.pending = to;
      if (c.state === "in") return; // 덮는 중이면 마지막 목적지만 기억
      const html = document.documentElement;
      c.state = "in";
      setCurtain("in");
      html.classList.add("ib-pt-run");
      c.timers.push(
        setTimeout(() => {
          const dest = c.pending;
          c.pending = null;
          navigate(dest);
          c.timers.push(
            setTimeout(() => {
              c.state = "out";
              setCurtain("out");
              c.timers.push(
                setTimeout(() => {
                  c.state = "idle";
                  setCurtain("idle");
                  html.classList.remove("ib-pt-run");
                }, CURTAIN_OUT)
              );
            }, 60)
          );
        }, CURTAIN_IN)
      );
    },
    [navigate]
  );

  // 3) 사이트 안 링크 가로채기 (새로고침 없는 이동, 같은 페이지 안 #앵커 포함)
  useEffect(() => {
    const onClick = (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target.closest ? e.target.closest("a[href]") : null;
      if (!a) return;
      const target = a.getAttribute("target");
      if ((target && target !== "_self") || a.hasAttribute("download")) return;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin || url.pathname.startsWith("/assets/")) return;
      const cur = window.location;
      const samePage = url.pathname === cur.pathname && url.search === cur.search;
      const to = url.pathname + url.search + url.hash;
      e.preventDefault();
      if (samePage) navigate(to, { replace: url.hash === cur.hash });
      else goWithCurtain(to);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [navigate, goWithCurtain]);

  // 4) 화면 맨 위 스크롤 진행선
  const progressRef = useRef(null);
  useEffect(() => {
    let raf = 0;
    const update = () => {
      raf = 0;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const p = max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0;
      if (progressRef.current) progressRef.current.style.setProperty("--sp", p.toFixed(4));
    };
    const req = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    window.addEventListener("scroll", req, { passive: true });
    window.addEventListener("resize", req);
    req();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", req);
      window.removeEventListener("resize", req);
    };
  }, [location.pathname]);

  // 5) 240px 이상 스크롤하면 body.ib-scrolled (HOME 모바일 빠른상담 표시 기준)
  useEffect(() => {
    const onScroll = () => document.body.classList.toggle("ib-scrolled", window.scrollY > 240);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <LightboxProvider>
      <PageScope page={page} pathname={location.pathname} />
      <div ref={progressRef} className="ib-progress" aria-hidden="true" />
      <Header currentKey={page.key} />
      <Outlet />
      <Footer />
      <QuickContact />
      <div className={"ib-pt" + (curtain !== "idle" ? ` is-${curtain}` : "")} aria-hidden="true">
        <span className="ib-pt__mark">INBA THAILAND</span>
      </div>
    </LightboxProvider>
  );
}
